// Photo capture -> PUT /api/photos/:id. "Resumable" per the API contract
// means a whole-file PUT with no Content-Range header is safe to retry from
// scratch: parseContentRange's default (start=0, end=len-1, total=len) plus
// the server always re-writing bytes [start, start+chunk.length) makes a
// second identical whole-file PUT overwrite the same range and land the same
// result — see photos-routes.ts. That's simpler and robust enough for
// phone-camera-sized photos, so this module never splits a file into chunks
// itself; it just makes one whole-file attempt and lets the caller (lib/
// sync.ts) decide what to do on failure (retry-queue, same as a run event).
import { api } from "./api";

export type PhotoPurpose = "start" | "end" | "qa" | "other";

/** The server gates a photo commit on this hash, so there is no uploading
 * without it. crypto.subtle is SECURE-CONTEXT ONLY — undefined over plain
 * http:// to a LAN IP — so check before dereferencing it and surface the real
 * reason (PhotoSlot renders a thrown error in its error state) instead of a
 * "Cannot read properties of undefined" crash. Same origin rule that costs
 * this app its service worker and camera; the fix is serving over HTTPS, not
 * a hand-rolled digest. */
export async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  if (!crypto.subtle) {
    throw new Error("Photos need a secure connection (https). Ask your manager for the https link.");
  }
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export type PhotoUploadResult = { id: string; committed: boolean };

/** One attempt at PUT /api/photos/:id with the whole file. Throws on a
 * non-2xx/308 response or a network failure — the caller decides whether to
 * retry immediately or queue for the background sync loop. */
export async function putPhotoOnce(
  photoId: string, bytes: Uint8Array<ArrayBuffer>, contentType: string, purpose: PhotoPurpose, deviceId: string,
): Promise<PhotoUploadResult> {
  const sha256 = await sha256Hex(bytes);
  const res = await api.putRaw(`/api/photos/${photoId}`, bytes, {
    "Content-Type": contentType,
    "X-Content-SHA256": sha256,
    "X-Photo-Purpose": purpose,
    "X-Device-Id": deviceId,
  });
  if (!res.ok && res.status !== 308) throw new Error(`photo PUT failed: HTTP ${res.status}`);
  const body = await res.json().catch(() => ({}) as { committed?: number });
  return { id: photoId, committed: body.committed === 1 };
}
